import { CollectionRulesService } from '@affine/core/modules/collection-rules';
import { DocsService } from '@affine/core/modules/doc';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { NavigationPanelDocNode } from '../../desktop/components/navigation-panel/nodes/doc';
import {
  NavigationPanelTreeRoot,
  NavigationPanelTreeSelectionProvider,
} from '../../desktop/components/navigation-panel/tree';
import { useSidebarSearchDocs } from '../sidebar-tree-search';
import * as styles from './documents-sidebar-content.css';
import { changedLabel, rowsShown } from './documents-sidebar-recent-rows';
import { organizeSections, sidebarOrganization } from './index.css';
import { RECENT_VIEW_PATH } from './use-documents-sidebar-view';

// What "All docs" lists, the document changed last on top.
const EVERY_DOCUMENT: Parameters<CollectionRulesService['watch']>[0] = {
  filters: [{ type: 'system', key: 'trash', method: 'is', value: 'false' }],
  orderBy: { type: 'system', key: 'updatedAt', desc: true },
  extraFilters: [
    { type: 'system', key: 'empty-journal', method: 'is', value: 'false' },
    { type: 'system', key: 'trash', method: 'is', value: 'false' },
  ],
};

function sameOrder(left: readonly string[] | undefined, right: string[]) {
  return (
    left?.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

/**
 * Every document of the workspace as sidebar rows, the one changed last on top. The rows exist
 * only while the list is on screen, and only as far as the user has scrolled.
 */
export function DocumentsSidebarRecent({ shown }: { shown: boolean }) {
  const t = useI18n();
  const rules = useService(CollectionRulesService);
  const docCache = useService(AccessibleDocCacheStore);
  const moreOnServer = useLiveData(docCache.hasNextMetadataPage$).all;
  const search = useSidebarSearchDocs();
  const [ordered, setOrdered] = useState<string[]>();
  useEffect(() => {
    if (!shown) return;
    const subscription = rules.watch(EVERY_DOCUMENT).subscribe({
      next: result => {
        const next = result.groups.flatMap(group => group.items);
        // Typing in a document changes its date over and over, but rarely the order.
        setOrdered(previous => (sameOrder(previous, next) ? previous : next));
      },
      error: error => console.error(error),
    });
    return () => subscription.unsubscribe();
  }, [rules, shown]);
  const listed = useMemo(() => {
    const all = ordered ?? [];
    return search.active ? all.filter(id => search.docs.has(id)) : all;
  }, [ordered, search.active, search.docs]);

  const [steps, setSteps] = useState(0);
  // A page may bring no row for this list; the next one is asked for all the same.
  const [pages, setPages] = useState(0);
  const rows = rowsShown(listed.length, steps);
  const more = rows < listed.length;
  const end = useRef<HTMLDivElement>(null);
  // Each further step observes anew, so a list that still ends in view goes on by itself.
  useEffect(() => {
    const mark = end.current;
    if (!shown || !mark || (!more && !moreOnServer)) return;
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        if (more) setSteps(count => count + 1);
        else
          docCache
            .fetchNextAccessibleDocsPage('all')
            .then(() => setPages(count => count + 1))
            .catch(console.error);
      },
      {
        root: mark.closest('[data-sidebar-scroll-viewport]'),
        rootMargin: '240px 0px',
      }
    );
    observer.observe(mark);
    return () => observer.disconnect();
  }, [docCache, more, moreOnServer, pages, rows, shown]);

  if (!shown) return null;
  return (
    <div className={sidebarOrganization} data-testid="documents-sidebar-recent">
      <NavigationPanelTreeSelectionProvider>
        <div className={organizeSections}>
          <NavigationPanelTreeRoot>
            {listed.slice(0, rows).map(docId => (
              <RecentDocument key={docId} docId={docId} />
            ))}
          </NavigationPanelTreeRoot>
          <div ref={end} aria-hidden="true" />
          {ordered && !more && !moreOnServer ? (
            <WorkbenchLink
              to="/all"
              className={styles.recentAll}
              data-testid="documents-sidebar-recent-all"
            >
              {t['com.streamlain.sidebar.documents.recent.all']()}
            </WorkbenchLink>
          ) : null}
        </div>
      </NavigationPanelTreeSelectionProvider>
    </div>
  );
}

const RecentDocument = memo(function RecentDocument({
  docId,
}: {
  docId: string;
}) {
  const changed = useMemo(() => <RecentChanged docId={docId} />, [docId]);
  return (
    <NavigationPanelDocNode
      docId={docId}
      parentPath={RECENT_VIEW_PATH}
      hideLinkedChildren
      disableDrag
      reorderable={false}
      trailing={changed}
    />
  );
});

// Follows the document's date on its own; the row does not render again for it.
function RecentChanged({ docId }: { docId: string }) {
  const record = useLiveData(useService(DocsService).list.doc$(docId));
  const changed = useLiveData(record?.updatedAt$);
  return changed ? (
    <time
      className={styles.recentChanged}
      dateTime={new Date(changed).toISOString()}
    >
      {changedLabel(changed)}
    </time>
  ) : null;
}
